import { createSelector } from '@reduxjs/toolkit';

import { getChipFromValue } from '../../../utils/chips';
import type { CoreRootState } from '../../CoreStore';
import { useCoreSelector } from '../../CoreStore';
import { selectBets } from '../bets/rawSelectors';
import type { IBetSpots } from '../bets/types';

import type { ChipValueType } from './types';
import { ChipTypes } from './types';

const chipTypeSelector = createSelector(
  (state: CoreRootState): ChipTypes | undefined => state.chips.selectedChip?.type,
  (state: CoreRootState): IBetSpots | undefined => selectBets(state)?.betSpots,
  (selectedChipType, betSpots): ChipTypes => {
    const placedBetType = Object.values(betSpots ?? {}).find((betSpot) => betSpot.value > 0)?.type;
    return placedBetType ?? selectedChipType ?? ChipTypes.CasinoChip;
  }
);
const chipSelector = createSelector(
  (state: CoreRootState, chipType: ChipTypes): ChipValueType[] => {
    if (chipType === ChipTypes.FreeChip) {
      return state.chips.chipsData.freeChips;
    }
    return state.chips.chipsData.casinoChips;
  },
  (state: CoreRootState, chipType: ChipTypes): ChipTypes => chipType,
  (state: CoreRootState, chipType: ChipTypes, value: number): number => value,
  (chips, chipType, value): ChipValueType => {
    return getChipFromValue(chips, value, chipType);
  }
);

export const useChipSlice = {
  getSelectedChip: (): ChipValueType => {
    return useCoreSelector((state) => state.chips.selectedChip || state.chips.chips[0]);
  },
  getChips: (): ChipValueType[] => {
    return useCoreSelector((state) => state.chips.chips);
  },
  getChipByValue: (value: number, overrideType?: ChipTypes): ChipValueType => {
    return useCoreSelector((state) => {
      const chipType = overrideType ?? chipTypeSelector(state);
      return chipSelector(state, chipType, value);
    });
  },
  getAreChipsReady: (): boolean => {
    return useCoreSelector((state) => state.chips.chipsReady);
  },
  isClipChipStackOpened: (): boolean => {
    return useCoreSelector((state) => state.chips.isClipChipStackOpened);
  },
  isRevolverChipStackOpened: (): boolean => {
    return useCoreSelector((state) => state.chips.isRevolverChipStackOpened);
  },
  getFreeChipsRemaining: (): number | undefined => {
    return useCoreSelector((state) => state.chips.chipsData?.freeChips[0]?.count);
  },
};
